import React, { useState } from 'react';
import { useAuth } from '../../context/authContext';
import { useDashboard } from '../../context/dashboardContext';
import { Eye, EyeOff, Lock, User, Mail, Calendar, Settings as SettingsIcon, Type } from 'lucide-react';
import toast from 'react-hot-toast';

const FONT_SIZE_OPTIONS = [
  { label: 'Small', value: 14 },
  { label: 'Medium', value: 16 },
  { label: 'Large', value: 18 },
  { label: 'Extra Large', value: 20 },
];

const Settings = () => {
  const { user } = useAuth();
  const { changeOwnPassword } = useDashboard();
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [fontSize, setFontSize] = useState(() => {
    return parseInt(localStorage.getItem('appFontSize')) || 16;
  });

  const handleFontSizeChange = (size) => {
    setFontSize(size);
    localStorage.setItem('appFontSize', size);
    document.documentElement.style.setProperty('--app-font-size', size + 'px');
  };

  const formattedDate = user?.createdAt 
    ? new Date(user.createdAt).toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      })
    : 'N/A';

  const handlePasswordReset = async () => {
    if (newPassword.trim().length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setLoading(true);
    try {
      const res = await changeOwnPassword(newPassword);
      if (res.success) {
        toast.success('Password updated successfully');
        setShowPasswordModal(false);
        setNewPassword('');
      } else {
        toast.error(res.error || 'Failed to update password');
      }
    } catch (error) {
      toast.error('An error occurred while updating password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-3xl mx-auto">
        {/* Centered heading section */}
        <div className="mb-6 mt-4 text-center">
          <div className="flex items-center justify-center gap-2">
            <SettingsIcon className="w-6 h-6 text-gray-700 animate-spin-slow" />
            <h1 className="text-2xl font-bold text-gray-900">Account Settings</h1>
          </div>
          <p className="text-gray-600 mt-1">Manage your account information and security</p>
        </div>

        <div className="bg-white rounded-xl shadow-md overflow-hidden">
          {/* Profile Header */}
          <div className="bg-gradient-to-r from-red-600 to-red-800 p-4 text-white">
            <div className="flex items-center">
              <div className="w-14 h-14 rounded-full bg-white/20 flex items-center justify-center">
                <User className="h-7 w-7" />
              </div>
              <div className="ml-3">
                <h2 className="text-lg font-semibold">{user?.name || 'User'}</h2>
                <p className="text-red-100 text-sm">{user?.email || 'No email provided'}</p>
              </div>
            </div>
          </div>

          {/* Account Information */}
          <div className="p-4">
            <h3 className="text-md font-medium text-gray-900 mb-3">Account Information</h3>
            
            <div className="space-y-3">
              <div className="flex items-start">
                <div className="flex-shrink-0 mt-1">
                  <User className="h-4 w-4 text-gray-400" />
                </div>
                <div className="ml-3">
                  <p className="text-xs font-medium text-gray-500">Full Name</p>
                  <p className="text-gray-900 text-sm">{user?.name || 'N/A'}</p>
                </div>
              </div>
              
              <div className="flex items-start">
                <div className="flex-shrink-0 mt-1">
                  <Mail className="h-4 w-4 text-gray-400" />
                </div>
                <div className="ml-3">
                  <p className="text-xs font-medium text-gray-500">Email Address</p>
                  <p className="text-gray-900 text-sm">{user?.email || 'N/A'}</p>
                </div>
              </div>
              
              <div className="flex items-start">
                <div className="flex-shrink-0 mt-1">
                  <Calendar className="h-4 w-4 text-gray-400" />
                </div>
                <div className="ml-3">
                  <p className="text-xs font-medium text-gray-500">Joined Date</p>
                  <p className="text-gray-900 text-sm">{formattedDate}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Security Section */}
          <div className="border-t border-gray-200 p-4">
            <h3 className="text-md font-medium text-gray-900 mb-3">Security</h3>

            <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <Lock className="h-4 w-4 text-gray-400" />
                  </div>
                  <div className="ml-3">
                    <p className="text-sm font-medium text-gray-900">Password</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowPasswordModal(true)}
                  className="px-3 py-1.5 text-xs font-medium text-white bg-red-600 rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                >
                  Change Password
                </button>
              </div>
            </div>
          </div>

          {/* Font Size Section */}
          <div className="border-t border-gray-200 p-4">
            <h3 className="text-md font-medium text-gray-900 mb-3">Display</h3>

            <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
              <div className="flex items-center mb-3">
                <Type className="h-4 w-4 text-gray-400" />
                <p className="text-sm font-medium text-gray-900 ml-3">Font Size</p>
              </div>

              <div className="grid grid-cols-4 gap-2">
                {FONT_SIZE_OPTIONS.map((option) => (
                  <button
                    key={option.value}
                    onClick={() => handleFontSizeChange(option.value)}
                    className={`px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                      fontSize === option.value
                        ? 'bg-red-600 text-white shadow-sm'
                        : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-100'
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>

              <p className="text-xs text-gray-500 mt-2">
                Changes apply to the entire application except the conveyor builder boxes.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Password Reset Modal */}
      {showPasswordModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
            <div className="p-5">
              <div className="flex items-center justify-center mb-5">
                <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                  <Lock className="h-5 w-5 text-red-600" />
                </div>
              </div>
              
              <h2 className="text-xl font-bold text-center text-gray-800 mb-2">Change Password</h2>
              <p className="text-center text-gray-600 text-sm mb-5">Set a new password for your account</p>

              <div className="mb-5">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  New Password
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-gray-800 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent text-sm"
                    placeholder="Enter new password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-500 hover:text-red-600"
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
                <p className="mt-1.5 text-xs text-gray-500">Password must be at least 6 characters</p>
              </div>

              <div className="flex flex-col sm:flex-row sm:justify-end gap-2 mt-6">
                <button
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                  onClick={() => {
                    setShowPasswordModal(false);
                    setNewPassword('');
                  }}
                >
                  Cancel
                </button>
                <button
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 flex items-center justify-center"
                  onClick={handlePasswordReset}
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Updating...
                    </>
                  ) : 'Change Password'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Settings;
